import { useEffect, useId, useRef, useState } from "react";

import TextInput from "../../../shared/ui/Field/TextInput";
import {
  searchWritableClients,
  unwrapClientResults,
  clientOptionLabel,
} from "../api/clientSearch";
import useDebounce from "../../../hooks/useDebounce";

const UNBOUND_ID = "__unbound__";

/**
 * Bounded server-backed CRM client combobox for presentation recipient stage.
 * Escape closes the popup first and must not bubble to the parent modal.
 * aria-selected reflects persisted selection only; aria-activedescendant +
 * .is-active reflect keyboard cursor.
 */
function PresentationClientCombobox({
  id = "presentation-client-search",
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
  selectedClient,
  onSelect,
  error,
  disabled = false,
  searchClients = null,
}) {
  const listId = useId();
  const rootRef = useRef(null);
  const optionRefs = useRef(new Map());
  const [query, setQuery] = useState("");
  const debouncedSearch = useDebounce(query, 300);
  const [options, setOptions] = useState([]);
  const [loadState, setLoadState] = useState("idle");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputInvalid = invalid || Boolean(error);
  const searchFn =
    typeof searchClients === "function" ? searchClients : searchWritableClients;

  useEffect(() => {
    const controller = new AbortController();
    setLoadState("loading");
    searchFn({ search: debouncedSearch }, { signal: controller.signal })
      .then((data) => {
        setOptions(unwrapClientResults(data));
        setLoadState("loaded");
      })
      .catch((err) => {
        if (err && err.code === "ERR_CANCELED") return;
        setLoadState("error");
      });
    return () => controller.abort();
  }, [debouncedSearch, searchFn]);

  const rows = [
    { id: UNBOUND_ID, label: "Без прив'язки до клієнта", unbound: true },
    ...options.map((client) => ({
      id: client.id,
      label: clientOptionLabel(client),
      client,
    })),
  ];

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      setOpen(false);
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open]);

  useEffect(() => {
    if (open) setActiveIndex(0);
  }, [open, options]);

  useEffect(() => {
    if (!open) return;
    const row = rows[activeIndex];
    if (!row) return;
    const node = optionRefs.current.get(row.id);
    if (node && typeof node.scrollIntoView === "function") {
      try {
        node.scrollIntoView({ block: "nearest" });
      } catch (_err) {
        try {
          node.scrollIntoView(false);
        } catch (_fallbackErr) {
          /* ignore */
        }
      }
    }
    // rows is rebuilt each render; depend on identity that changes active option.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, open, options, listId]);

  const choose = (row) => {
    if (row.unbound) {
      onSelect?.(null);
    } else {
      onSelect?.(row.client);
    }
    setOpen(false);
    setQuery("");
  };

  const onInputKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((index) => Math.min(index + 1, rows.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === "Enter" && open) {
      event.preventDefault();
      const row = rows[activeIndex];
      if (row) choose(row);
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    }
  };

  const activeId = open && rows[activeIndex]
    ? `${listId}-option-${rows[activeIndex].id}`
    : undefined;

  return (
    <div
      ref={rootRef}
      className="presentation-combobox"
      data-testid="presentation-client-combobox"
      data-open={open ? "true" : "false"}
    >
      <TextInput
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        aria-describedby={describedBy}
        aria-invalid={inputInvalid ? "true" : undefined}
        value={query}
        disabled={disabled}
        placeholder="Пошук клієнта…"
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={onInputKeyDown}
      />
      {selectedClient ? (
        <div className="presentation-client-selected" data-testid="presentation-client-selected">
          <span>{selectedClient.name}</span>
          <button
            type="button"
            className="action-btn"
            onClick={() => onSelect?.(null)}
          >
            Зняти прив'язку
          </button>
        </div>
      ) : (
        <p className="presentation-editor__help">Клієнта не вибрано.</p>
      )}
      {open ? (
        <div
          id={listId}
          className="presentation-combobox__popup"
          role="listbox"
          aria-label="Клієнти"
          data-testid="presentation-client-listbox"
        >
          {loadState === "loading" ? (
            <p className="presentation-combobox__status" role="status">
              Завантаження клієнтів…
            </p>
          ) : null}
          {loadState === "error" ? (
            <p className="presentation-combobox__status presentation-form__error" role="alert">
              Не вдалося завантажити клієнтів.
            </p>
          ) : null}
          {rows.map((row, index) => {
            const selected = row.unbound
              ? !selectedClient
              : Boolean(selectedClient && selectedClient.id === row.id);
            return (
              <button
                key={row.id}
                id={`${listId}-option-${row.id}`}
                ref={(node) => {
                  if (node) optionRefs.current.set(row.id, node);
                  else optionRefs.current.delete(row.id);
                }}
                type="button"
                role="option"
                aria-selected={selected}
                className={[
                  "presentation-combobox__option",
                  row.unbound ? "presentation-combobox__option--unbound" : "",
                  selected ? "is-selected" : "",
                  index === activeIndex ? "is-active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(row)}
              >
                {row.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default PresentationClientCombobox;
